import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import type { SelectionContext } from '@atlaskit/editor-common/types';
import type { DocNode } from '@atlaskit/adf-schema';

import {
	RemixContainer,
	type RemixOptionSelectionParameters,
} from '@atlassian/malleable-ui-components';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { RendererRemixHighlightStyles } from './RendererRemixHighlightStyles.tsx';
import { useRendererRemixHighlight } from './useRendererRemixHighlight.ts';
import type { DynamicUiSource } from './common-types.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';

const constructDynamicUiSource = (
	adfDocument: DocNode,
	selectionContext: SelectionContext | null,
): DynamicUiSource | undefined => {
	if (!selectionContext) {
		return undefined;
	}

	return {
		contentType: 'page' as const,
		startLocalId: selectionContext.localIds?.[0] ?? '',
		endLocalId: selectionContext.localIds?.[selectionContext.localIds.length - 1] ?? '',
		startIndex: selectionContext.startIndex ?? 0,
		endIndex: selectionContext.endIndex ?? -1,
		// TODO MAX-103: Filter out unsupported nodes
		fragmentAdf: selectionContext.selectionFragment,
		fullAdf: adfDocument,
	};
};

type RendererRemixCreatorProps = {
	adfDocument: DocNode;
	selectionContext: SelectionContext | null;
	onClose: (options?: { preserveHighlight?: boolean }) => void;
	onStartThinking: () => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
};

const RendererRemixModal = ({
	adfDocument,
	selectionContext,
	onClose,
	onStartThinking,
	onStopThinking,
}: RendererRemixCreatorProps) => {
	const { sendAIFCPrompt, stopRemixing, isRovoResponding, openAIFCStagingModal } = useRemixToAIFC();

	const isRemixCancelled = useRef(false);

	const onThinkingStateChange = useCallback(
		(isThinking: boolean) => {
			if (isThinking) {
				isRemixCancelled.current = false;
				onStartThinking();
			} else {
				isRemixCancelled.current = true;
				stopRemixing();
				onStopThinking({ reopenModal: true });
			}
		},
		[onStartThinking, stopRemixing, onStopThinking],
	);

	const prevIsRovoResponding = useRef(false);
	useEffect(() => {
		// Only open the staging modal when the smart creation finished processing the prompt
		if (!isRemixCancelled.current && !prevIsRovoResponding.current && isRovoResponding) {
			openAIFCStagingModal();
			onClose({ preserveHighlight: true });
		}
		prevIsRovoResponding.current = isRovoResponding;
	}, [isRovoResponding, openAIFCStagingModal, onClose]);

	const handleOptionSelect = useCallback(
		async ({ type, userPrompt }: RemixOptionSelectionParameters) => {
			const dynamicUiSource = constructDynamicUiSource(adfDocument, selectionContext);
			await sendAIFCPrompt({
				userPrompt,
				dynamicUiType: type,
				dynamicUiSource,
			});
		},
		[sendAIFCPrompt, adfDocument, selectionContext],
	);

	return (
		<RemixContainer
			selectedText=""
			onClose={() => onClose()}
			onOptionSelect={handleOptionSelect}
			onThinkingStateChange={onThinkingStateChange}
		/>
	);
};

type RendererRemixContextProps = {
	openRemixModal: (adfDocument: DocNode, selection: SelectionContext | null) => void;
	clearRemixHighlight: () => void;
};
const RendererRemixContext = createContext<RendererRemixContextProps>({
	openRemixModal: () => {},
	clearRemixHighlight: () => {},
});

export const useRendererRemixContext = () => {
	return useContext(RendererRemixContext);
};

const RendererRemixContainerImpl = ({ children }: { children: React.ReactNode }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [adfDocument, setAdfDocument] = useState<DocNode | null>(null);
	const [selectionContext, setSelectionContext] = useState<SelectionContext | null>(null);
	const [isThinking, setIsThinking] = useState(false);
	const { startHighlight, clearHighlight } = useRendererRemixHighlight();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const prevShowSmartCreationModal = useRef(showSmartCreationModal);

	useEffect(() => {
		if (prevShowSmartCreationModal.current && !showSmartCreationModal) {
			clearHighlight();
		}
		prevShowSmartCreationModal.current = showSmartCreationModal;
	}, [showSmartCreationModal, clearHighlight]);

	const openModal = useCallback(
		(adfDocument: DocNode, selection: SelectionContext | null) => {
			setAdfDocument(adfDocument);
			startHighlight();
			setSelectionContext(selection);
			setIsModalOpen(true);
		},
		[startHighlight],
	);

	const closeModal = useCallback(
		(options?: { preserveHighlight?: boolean }) => {
			if (!options?.preserveHighlight) {
				clearHighlight();
			}
			setAdfDocument(null);
			setSelectionContext(null);
			setIsModalOpen(false);
			setIsThinking(false);
		},
		[clearHighlight],
	);

	const onStartThinking = useCallback(() => {
		setIsThinking(true);
		setIsModalOpen(false);
	}, []);

	const onStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		setIsThinking(false);
		if (options?.reopenModal) {
			setIsModalOpen(true);
		}
	}, []);

	const context = useMemo(() => {
		return { openRemixModal: openModal, clearRemixHighlight: clearHighlight };
	}, [openModal, clearHighlight]);

	return (
		<RendererRemixContext.Provider value={context}>
			<RendererRemixHighlightStyles />
			{children}
			{(isModalOpen || isThinking) && adfDocument && (
				<ConfluenceConversationAssistant>
					<RendererRemixModal
						adfDocument={adfDocument}
						selectionContext={selectionContext}
						onClose={closeModal}
						onStartThinking={onStartThinking}
						onStopThinking={onStopThinking}
					/>
				</ConfluenceConversationAssistant>
			)}
		</RendererRemixContext.Provider>
	);
};

export const RendererRemixContainer = ({ children }: { children: React.ReactNode }) => {
	if (!expVal('cc-maui-experiment', 'isEnabled', false)) {
		return <>{children}</>;
	}

	return <RendererRemixContainerImpl>{children}</RendererRemixContainerImpl>;
};
